import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import {
  LayoutDashboard,
  FileText,
  Users,
  Settings,
  Bell,
  LogOut,
  Moon,
  Sun,
  Languages,
  Menu,
  X as XIcon,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useTheme } from '@/components/theme-provider';
import { t } from '@/i18n/translations';
import { notificationAPI } from '@/services/api';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { BrandLogo } from '@/components/layout/BrandLogo';

export default function Layout() {
  const { user, logout, lang, setLang } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [notifications, setNotifications] = useState([]);
  /** Em ecrãs &lt; lg o menu é uma gaveta; em desktop fica sempre visível. */
  const [isLgUp, setIsLgUp] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches,
  );
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const onChange = () => {
      setIsLgUp(mq.matches);
      if (mq.matches) setMobileNavOpen(false);
    };
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (!isLgUp && mobileNavOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev;
      };
    }
    return undefined;
  }, [isLgUp, mobileNavOpen]);

  useEffect(() => {
    if (typeof document === 'undefined' || isLgUp || !mobileNavOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setMobileNavOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isLgUp, mobileNavOpen]);

  useEffect(() => {
    const fetchNotifications = () => {
      notificationAPI.unreadCount().then((r) => setUnread(r.data.count)).catch(() => {});
      notificationAPI.list().then((r) => setNotifications(r.data.slice(0, 25))).catch(() => {});
    };
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const markAllRead = async () => {
    try {
      await notificationAPI.markAllRead();
      setUnread(0);
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch {
      /* ignore */
    }
  };

  // Apaga uma notificação individual. Optimistic update — se a chamada
  // falhar, voltamos a buscar a lista do servidor para reconciliar.
  const deleteNotification = async (id) => {
    const removed = notifications.find((n) => n.id === id);
    setNotifications((prev) => prev.filter((n) => n.id !== id));
    if (removed && !removed.read) setUnread((prev) => Math.max(0, prev - 1));
    try {
      await notificationAPI.remove(id);
    } catch {
      // Fallback: re-sync caso o backend rejeite.
      notificationAPI.list().then((r) => setNotifications(r.data.slice(0, 25))).catch(() => {});
      notificationAPI.unreadCount().then((r) => setUnread(r.data.count)).catch(() => {});
    }
  };

  const deleteAllNotifications = async () => {
    setNotifications([]);
    setUnread(0);
    try {
      await notificationAPI.removeAll();
    } catch {
      notificationAPI.list().then((r) => setNotifications(r.data.slice(0, 25))).catch(() => {});
      notificationAPI.unreadCount().then((r) => setUnread(r.data.count)).catch(() => {});
    }
  };

  const navItems = [
    { to: '/', label: t('dashboard', lang), icon: LayoutDashboard },
    { to: '/rfqs', label: t('rfqs', lang), icon: FileText },
    { to: '/clients', label: t('clients', lang), icon: Users },
  ];
  if (user?.is_admin) {
    navItems.push({ to: '/admin', label: t('admin', lang), icon: Settings });
  }

  const initials = user?.full_name
    ? user.full_name
        .split(' ')
        .slice(0, 2)
        .map((s) => s[0])
        .join('')
        .toUpperCase()
    : '?';

  return (
    <div className="flex min-h-screen bg-background text-foreground">
      <a href="#main" className="skip-link">
        {t('skip_to_content', lang)}
      </a>

      {!isLgUp && mobileNavOpen && (
        <button
          type="button"
          className="fixed inset-0 z-30 bg-black/40 backdrop-blur-[1px] lg:hidden"
          aria-label={t('sidebar_close_menu', lang)}
          onClick={() => setMobileNavOpen(false)}
        />
      )}

      <aside
        id="app-sidebar"
        className={cn(
          'fixed inset-y-0 left-0 z-40 flex w-60 max-w-[85vw] flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground transition-transform duration-200 ease-out',
          isLgUp ? 'translate-x-0' : mobileNavOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="flex h-16 shrink-0 items-center gap-2 border-b border-sidebar-border px-3 sm:px-4">
          <div className="flex min-w-0 flex-1 items-center gap-2.5">
            <BrandLogo onDark imgClassName="h-6" alt="" />
            <div className="min-w-0">
              <div className="truncate text-[15px] font-semibold tracking-tight">HPDC RFQ</div>
              <div className="truncate text-[10px] font-medium uppercase tracking-[0.12em] text-sidebar-foreground/45">
                Aluthea
              </div>
            </div>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="shrink-0 lg:hidden"
            onClick={() => setMobileNavOpen(false)}
            aria-label={t('sidebar_close_menu', lang)}
          >
            <XIcon className="h-4 w-4" />
          </Button>
        </div>
        <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4" aria-label={t('main_nav_label', lang)}>
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                onClick={() => {
                  if (!isLgUp) setMobileNavOpen(false);
                }}
                className={({ isActive }) =>
                  cn(
                    'relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-sidebar-accent text-sidebar-accent-foreground shadow-[inset_3px_0_0_0_hsl(var(--primary))]'
                      : 'text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground'
                  )
                }
              >
                <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>
        <div className="border-t border-sidebar-border p-4">
          <div className="flex items-center gap-3">
            <Avatar className="h-9 w-9">
              <AvatarFallback style={user?.avatar_color ? { background: user.avatar_color } : undefined}>
                {initials}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold">{user?.full_name}</div>
              <div className="truncate text-xs text-sidebar-foreground/60">{user?.username}</div>
            </div>
          </div>
        </div>
      </aside>

      <div
        className={cn(
          'flex min-h-screen flex-1 flex-col transition-[padding] duration-200 ease-out',
          isLgUp ? 'pl-60' : 'pl-0',
        )}
      >
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between gap-3 border-b border-border/80 bg-background/80 px-4 backdrop-blur-md supports-[backdrop-filter]:bg-background/70 sm:gap-4 sm:px-6">
          <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-3">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="shrink-0 lg:hidden"
              onClick={() => setMobileNavOpen(true)}
              aria-expanded={mobileNavOpen}
              aria-controls="app-sidebar"
              aria-label={t('sidebar_open_menu', lang)}
            >
              <Menu className="h-5 w-5" />
            </Button>
            <div className="truncate text-sm font-medium tracking-tight text-foreground">
              {t('app_title', lang)}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Select value={lang} onValueChange={(v) => setLang(v)}>
              <SelectTrigger
                className="h-9 w-[96px] gap-1"
                aria-label={t('language_aria', lang)}
              >
                <Languages className="h-4 w-4 opacity-70" aria-hidden="true" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pt">{t('portuguese_full', lang)}</SelectItem>
                <SelectItem value="fr">{t('french_full', lang)}</SelectItem>
                <SelectItem value="en">{t('english_full', lang)}</SelectItem>
              </SelectContent>
            </Select>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={toggleTheme}
                  aria-label={theme === 'dark' ? t('toggle_to_light', lang) : t('toggle_to_dark', lang)}
                >
                  {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {theme === 'dark' ? t('light_mode', lang) : t('dark_mode', lang)}
              </TooltipContent>
            </Tooltip>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={t('notifications', lang) + (unread > 0 ? ` (${unread} ${t('unread_suffix', lang)})` : '')}
                  className="relative"
                >
                  <Bell className="h-4 w-4" />
                  {unread > 0 && (
                    <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-destructive-foreground">
                      {unread > 9 ? '9+' : unread}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className={cn(
                  // Largura responsiva: mais espaço para ler o texto completo
                  // em desktop; em ecrãs estreitos não ultrapassa a viewport.
                  'min-w-[18rem] max-w-[min(28rem,calc(100vw-1.5rem))] p-0',
                )}
              >
                <div className="border-b border-border px-2 py-2">
                  <DropdownMenuLabel className="flex flex-col gap-2 p-0 normal-case tracking-normal sm:flex-row sm:items-center sm:justify-between">
                    <span>{t('notifications', lang)}</span>
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      {unread > 0 && (
                        <button
                          type="button"
                          onClick={markAllRead}
                          className="text-xs font-normal text-primary normal-case tracking-normal hover:underline"
                        >
                          {t('mark_all_read', lang)}
                        </button>
                      )}
                      {notifications.length > 0 && (
                        <button
                          type="button"
                          onClick={deleteAllNotifications}
                          className="text-xs font-normal text-destructive normal-case tracking-normal hover:underline"
                        >
                          {t('clear_all_notifications', lang)}
                        </button>
                      )}
                    </div>
                  </DropdownMenuLabel>
                </div>
                {notifications.length === 0 ? (
                  <div className="px-3 py-8 text-center text-sm text-muted-foreground">
                    {t('no_notifications', lang)}
                  </div>
                ) : (
                  <div
                    className="max-h-[min(32rem,70vh)] overflow-y-auto overflow-x-hidden py-1"
                    role="list"
                    aria-label={t('notifications', lang)}
                  >
                    {notifications.map((n) => (
                      // Não usamos DropdownMenuItem aqui porque ele fecha o
                      // menu em qualquer click. Queremos que o botão "X"
                      // apague a notificação sem fechar a lista, pelo que
                      // renderizamos um wrapper estático e deixamos o
                      // event.stopPropagation() no botão.
                      <div
                        key={n.id}
                        role="listitem"
                        className={cn(
                          'flex items-start justify-between gap-2 border-b border-border/40 px-3 py-2.5 text-sm last:border-b-0',
                          !n.read && 'bg-accent/50',
                        )}
                      >
                        <div className="min-w-0 flex-1 space-y-1 text-left">
                          {(n.title || n.message) && (
                            <p className="whitespace-normal break-words font-medium leading-snug">
                              {n.title || n.message}
                            </p>
                          )}
                          {n.message && n.title && (
                            <p className="whitespace-normal break-words text-xs leading-relaxed text-muted-foreground">
                              {n.message}
                            </p>
                          )}
                        </div>
                        <button
                          type="button"
                          aria-label={t('delete_notification', lang)}
                          title={t('delete_notification', lang)}
                          className="mt-0.5 shrink-0 rounded p-0.5 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            deleteNotification(n.id);
                          }}
                        >
                          <XIcon className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleLogout}
                  aria-label={t('logout', lang)}
                >
                  <LogOut className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t('logout', lang)}</TooltipContent>
            </Tooltip>
          </div>
        </header>

        <main id="main" className="flex-1 px-5 py-6 sm:px-6 sm:py-7" tabIndex={-1}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
